import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { getCompanyCamProject, getCompanyCamProjectList, getCompanyCamPhotos, getCompanyCamDocuments, getCompanyCamPhoto } from "@/network/network";
import { debounce } from "@/other/utils/otherUtils";
import { formatFile } from "@/modern/utils";
import { FormattedFile } from "@/react/components/entityViews/files/context/EntityFileContext";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { EntityRecord } from "@/types/entity";
import { KeyValueModel } from "@/bryntum-lib/models/KeyValueModel.jsx";

type Address = {
  city?: string;
  country?: string;
  postal_code?: string;
  state?: string;
  street_address_1?: string;
  street_address_2?: string;
};

export interface ICCProject {
  id: number;
  name: string;
  address: Address;
  imageUrl: string;
  creatorName: string;
  updatedAt: string;
}

export interface ICCProjectApiData {
  id: number;
  name: string;
  address: Address;
  feature_image: { url: string }[];
  creator_name: string;
  updated_at: string;
  photo_count?: number;
}

export interface ICCPhoto {
  id: string;
  name?: string; // only in company cam pdf files
  company_id?: string;
  creator_id?: string;
  creator_type?: string;
  creator_name: string;
  project_id?: string;
  coordinates?: {
    lat: number;
    lon: number;
  };
  status?: string;
  uris?: Array<{
    type: string;
    uri: string;
    url: string;
  }>;
  url?: string; // only in company cam pdf files
  hash?: string;
  internal?: boolean;
  photo_url?: string;
  captured_at?: number;
  created_at: number;
  updated_at: number;
  byte_size?: number;
  processing_status?: string;
  description?: string | null;
}

const COMPANY_CAM_PROJECT_LIST_PER_PAGE = 25;

export const useSwivuCompanyCam = ({ record }: { record: EntityRecord }) => {
  const { project, user } = useSwivuBryntumContext();
  // const integrationStore = project.getCrudStore(
  //   STORE_KEYS.extraBoardIntegrationsStore
  // );
  const companyCamTokenRecord = useMemo(() => project.extraBoardIntegrationsStore.find((r) => r.key === "companyCam"), []);
  const { version } = useSwivuStore<KeyValueModel>({
    storeId: STORE_KEYS.extraBoardIntegrationsStore,
    subscriberFn: ({ record: changedRecord }) => {
      return changedRecord.id === companyCamTokenRecord?.id;
    }
  });

  // Project list states (for search/selection)
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [ccProjects, setCcProjects] = useState<ICCProject[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isResettingProjects, setIsResettingProjects] = useState(false);
  const [shouldFetchFreshProjects, setShouldFetchFreshProjects] = useState(false);
  const activeSearchIdRef = useRef(0);

  // Current project states (auto-fetched from record)
  const [currentCCProject, setCurrentCCProject] = useState<ICCProjectApiData | null>(null);
  const [isFetchingCurrentProject, setIsFetchingCurrentProject] = useState(false);

  // Photos states (for infinite scroll)
  const [companyCamPhotos, setCompanyCamPhotos] = useState<FormattedFile[]>([]);
  const [isFetchingPhotos, setIsFetchingPhotos] = useState(false);
  const [photosPage, setPhotosPage] = useState(1);
  const [hasMorePhotos, setHasMorePhotos] = useState(true);

  // Documents states (for infinite scroll)
  const [companyCamDocuments, setCompanyCamDocuments] = useState<FormattedFile[]>([]);
  const [isFetchingDocuments, setIsFetchingDocuments] = useState(false);
  const [documentsPage, setDocumentsPage] = useState(1);
  const [hasMoreDocuments, setHasMoreDocuments] = useState(true);

  const hasCompanyCamToken = useMemo(() => {
    return companyCamTokenRecord?.value !== undefined;
  }, [version, companyCamTokenRecord]);

  const getCompanyCamProjects = useCallback(async () => {
    const requestSearchId = activeSearchIdRef.current;
    if (!hasCompanyCamToken) {
      setHasMore(false);
      setLoading(false);
      if (requestSearchId === activeSearchIdRef.current) {
        setIsResettingProjects(false);
      }
      return;
    }

    setLoading(true);

    try {
      const { data } = (await getCompanyCamProjectList({ page, query: searchQuery }, companyCamTokenRecord?.value)) as { data?: ICCProjectApiData[] };

      if (data && data.length > 0) {
        const mappedProjects = data.map((project) => ({
          id: project.id,
          name: project.name,
          address: project.address,
          imageUrl: project.feature_image[2]?.url || "https://ps.w.org/replace-broken-images/assets/icon-256x256.png",
          creatorName: project.creator_name,
          updatedAt: project.updated_at
        }));

        setCcProjects((prev) => {
          if (requestSearchId !== activeSearchIdRef.current) {
            return prev;
          }
          return [...prev, ...mappedProjects];
        });

        if (requestSearchId === activeSearchIdRef.current) {
          setPage((prev) => prev + 1);
          if (data.length < COMPANY_CAM_PROJECT_LIST_PER_PAGE) {
            setHasMore(false);
          }
        }
      } else if (requestSearchId === activeSearchIdRef.current) {
        setHasMore(false);
      }
    } catch (err) {
      console.log(err);
      if (requestSearchId === activeSearchIdRef.current) {
        setHasMore(false);
      }
    } finally {
      setLoading(false);
      if (requestSearchId === activeSearchIdRef.current) {
        setIsResettingProjects(false);
      }
    }
  }, [hasCompanyCamToken, companyCamTokenRecord?.value, page, searchQuery]);

  const getCompanyCamProjectById = useCallback(
    async (companyCamProjectId: number): Promise<ICCProjectApiData> => {
      if (!hasCompanyCamToken || !companyCamTokenRecord?.value) {
        throw new Error("Company Cam token not available");
      }
      try {
        const response = await getCompanyCamProject(companyCamProjectId, companyCamTokenRecord?.value);
        if (!response.data) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.data;
      } catch (err) {
        console.log(err);
        throw err;
      }
    },
    [hasCompanyCamToken, companyCamTokenRecord?.value]
  );

  // Fetch current project (can be called manually or auto-fetched)
  const fetchCurrentProject = useCallback(async () => {
    if (!record.companyCamProject || !hasCompanyCamToken || !companyCamTokenRecord?.value) {
      setCurrentCCProject(null);
      return null;
    }

    setIsFetchingCurrentProject(true);
    try {
      const projectData = await getCompanyCamProjectById(record.companyCamProject);
      setCurrentCCProject(projectData);
      return projectData;
    } catch (err) {
      console.log(err);
      setCurrentCCProject(null);
      throw err;
    } finally {
      setIsFetchingCurrentProject(false);
    }
  }, [record.companyCamProject, hasCompanyCamToken, companyCamTokenRecord?.value, getCompanyCamProjectById]);

  // Fetch Company Cam photos (for infinite scroll)
  const fetchCompanyCamPhotos = useCallback(
    async (perPage: number = 25) => {
      if (!hasCompanyCamToken || !companyCamTokenRecord?.value) {
        console.log("Company Cam token not available");
        setHasMorePhotos(false);
        return;
      }

      if (!record.companyCamProject) {
        console.log("No Company Cam project loaded");
        setHasMorePhotos(false);
        return;
      }

      if (isFetchingPhotos) {
        return; // Prevent duplicate requests
      }

      setIsFetchingPhotos(true);
      try {
        const response = await getCompanyCamPhotos(
          record.companyCamProject,
          companyCamTokenRecord?.value as string,
          {
            per_page: perPage,
            page: photosPage
          } as { per_page: number; page: number }
        );

        const newPhotos = formatFile(response.data || [], user) || [];

        if (newPhotos.length > 0) {
          setCompanyCamPhotos((prev) => {
            const updatedPhotos = [...prev, ...newPhotos];

            // Check if we've fetched all photos by comparing with photo_count from current project
            if (currentCCProject?.photo_count && updatedPhotos.length >= currentCCProject.photo_count) {
              setHasMorePhotos(false);
            }

            return updatedPhotos;
          });
          setPhotosPage((prev) => prev + 1);

          // Also check if returned photos are less than requested (reached end of pagination)
          if (newPhotos.length < perPage) {
            setHasMorePhotos(false);
          }
        } else {
          setHasMorePhotos(false);
        }
      } catch (err) {
        console.log(err);
        throw err;
      } finally {
        setIsFetchingPhotos(false);
      }
    },
    [hasCompanyCamToken, companyCamTokenRecord?.value, record.companyCamProject, photosPage, isFetchingPhotos, currentCCProject]
  );

  // Fetch Company Cam documents (for infinite scroll)
  const fetchCompanyCamDocuments = useCallback(
    async (perPage: number = 25) => {
      if (!hasCompanyCamToken || !companyCamTokenRecord?.value) {
        console.log("Company Cam token not available");
        setHasMoreDocuments(false);
        return;
      }

      if (!record.companyCamProject) {
        console.log("No Company Cam project loaded");
        setHasMoreDocuments(false);
        return;
      }

      if (isFetchingDocuments) {
        return; // Prevent duplicate requests
      }

      setIsFetchingDocuments(true);
      try {
        const response = await getCompanyCamDocuments(
          record.companyCamProject,
          companyCamTokenRecord?.value as string,
          {
            per_page: perPage,
            page: documentsPage
          } as { per_page: number; page: number }
        );

        const newDocuments = formatFile(response.data || [], user) || [];

        if (newDocuments.length > 0) {
          setCompanyCamDocuments((prev) => [...prev, ...newDocuments]);
          setDocumentsPage((prev) => prev + 1);

          // Check if returned documents are less than requested (reached end of pagination)
          if (newDocuments.length < perPage) {
            setHasMoreDocuments(false);
          }
        } else {
          setHasMoreDocuments(false);
        }
      } catch (err) {
        console.log(err);
        throw err;
      } finally {
        setIsFetchingDocuments(false);
      }
    },
    [hasCompanyCamToken, companyCamTokenRecord?.value, record.companyCamProject, documentsPage, isFetchingDocuments, user]
  );

  // Reset photos (call when project changes or need to refresh)
  const resetCompanyCamPhotos = useCallback(() => {
    setCompanyCamPhotos([]);
    setPhotosPage(1);
    setHasMorePhotos(true);
  }, []);

  // Reset documents (call when project changes or need to refresh)
  const resetCompanyCamDocuments = useCallback(() => {
    setCompanyCamDocuments([]);
    setDocumentsPage(1);
    setHasMoreDocuments(true);
  }, []);

  const unsetCompanyCamProject = useCallback(() => {
    record.companyCamProject = null;
  }, [record]);

  const setCompanyCamProject = useCallback(
    (ccProjectId: number) => {
      record.companyCamProject = ccProjectId;
    },
    [record]
  );

  // Fetch a single CompanyCam photo by ID
  const fetchCompanyCamPhotoById = useCallback(
    async (photoId: string | number): Promise<FormattedFile | null> => {
      if (!hasCompanyCamToken || !companyCamTokenRecord?.value) {
        console.log("Company Cam token not available");
        return null;
      }

      try {
        const response = await getCompanyCamPhoto(photoId, companyCamTokenRecord.value as string);
        const photoData = response.data;

        if (!photoData) {
          return null;
        }

        // Format the photo using formatFile utility
        const formattedPhotos = formatFile([photoData], user);
        return formattedPhotos[0] || null;
      } catch (err) {
        console.error("Error fetching CompanyCam photo:", err);
        return null;
      }
    },
    [hasCompanyCamToken, companyCamTokenRecord?.value, user]
  );

  const debouncedSearch = useMemo(
    () =>
      debounce(() => {
        activeSearchIdRef.current += 1;
        setIsResettingProjects(true);
        setPage(1);
        setCcProjects([]);
        setHasMore(true);
        setShouldFetchFreshProjects(true);
      }, 300),
    []
  );

  const onSearch = useCallback(
    (query: string) => {
      setSearchQuery(query);
      debouncedSearch();
    },
    [debouncedSearch]
  );

  useEffect(() => {
    if (!shouldFetchFreshProjects) {
      return;
    }
    getCompanyCamProjects();
    setShouldFetchFreshProjects(false);
  }, [shouldFetchFreshProjects, getCompanyCamProjects]);

  return {
    // Project list (for search/selection)
    ccProjects,
    loading,
    hasMore,
    searchQuery,
    isResettingProjects,
    getCompanyCamProjects,
    onSearch,

    // Current project (auto-fetched)
    currentCCProject,
    isFetchingCurrentProject,
    fetchCurrentProject,

    // Photos (infinite scroll)
    companyCamPhotos,
    isFetchingPhotos,
    hasMorePhotos,
    fetchCompanyCamPhotos,
    resetCompanyCamPhotos,

    // Documents (infinite scroll)
    companyCamDocuments,
    isFetchingDocuments,
    hasMoreDocuments,
    fetchCompanyCamDocuments,
    resetCompanyCamDocuments,

    // Project operations
    getCompanyCamProjectById,
    setCompanyCamProject,
    unsetCompanyCamProject,

    // Photo operations
    fetchCompanyCamPhotoById,

    // Token status
    hasCompanyCamToken
  };
};
